import { evaluate } from "@kyan/ui";

import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";
import { useComboPrices } from "#/store/combo/comboPrices";
import { ComboPricesRowType } from "#/types/combo";
import {
  createOptionInstrumentName,
  createPerpInstrumentName,
  isOptionInstrument,
} from "#/utils/instrument.utils";
import { isBuy } from "#/utils/marketActionConverter";
import { formatCurrency } from "#/utils/valueFormatters";

export const useInstrumentsController = () => {
  const { comboInstruments } = useComboInstrumentsController();
  const { comboPrices: comboPricesFromWorker, limitNetPrices } =
    useComboPrices();

  const filteredComboPricesFromWorker = comboPricesFromWorker.filter(
    ({ rowType }) => rowType !== ComboPricesRowType.Summary,
  );

  const optionInstrumentsData = filteredComboPricesFromWorker
    .filter(({ instrument }) => isOptionInstrument(instrument))
    .map(({ instrument, price, side }) => {
      const size = comboInstruments.find(
        ({ optionType, underlying, strike, maturity }) => {
          const instrumentName = createOptionInstrumentName({
            optionType,
            underlying,
            strike,
            maturity,
          });

          return instrument === instrumentName;
        },
      )!.size;

      return {
        instrument,
        price,
        isBuySide: isBuy(side!),
        size,
      };
    });

  const [perpInstrumentData] = filteredComboPricesFromWorker
    .filter(({ instrument }) => !isOptionInstrument(instrument))
    .map(({ instrument, side }) => {
      const size = comboInstruments.find(({ underlying }) => {
        return createPerpInstrumentName(underlying) === instrument;
      })!.size;

      return {
        instrument,
        isBuySide: isBuy(side!),
        size,
      };
    });

  const netPremium =
    comboPricesFromWorker.find(
      ({ rowType }) => rowType === ComboPricesRowType.Summary,
    )!.netPremium ?? 0;
  const limitNetPriceVariant = evaluate({
    credit: netPremium > 0,
    debit: netPremium < 0,
    even: netPremium === 0,
  });

  const optionLimitPrice = formatCurrency(Number(limitNetPrices.summary), {
    precision: 0,
  });
  const perpLimitPrice = formatCurrency(Number(limitNetPrices.perpsLeg), {
    precision: 0,
  });

  return {
    perpLimitPrice,
    optionLimitPrice,
    perpInstrumentData,
    limitNetPriceVariant,
    optionInstrumentsData,
  };
};
